import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  MenuItem,
  TablePagination,
  InputLabel,
  FormControl,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import configServ from "../../services/config";
import BasicRangeShortcuts from "./BasicRangeShortcuts";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
const AppointmentTable = ({
  handleSearch,
  handleChangePage,
  handleChangeRowsPerPage,
  loading,
  total,
  searchBy,
  setSearchBy,
  setCurrentPage,
  search,
  appointments,
  perPage,
  currentPage,
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  setSearch,
}) => {
  const [openDateDialog, setOpenDateDialog] = useState(false);

  const handleOpenDateDialog = () => setOpenDateDialog(true);
  const handleCloseDateDialog = () => setOpenDateDialog(false);
  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSearchBy("patient")
    setSearch("")
  };
  return (
    <Box p={2} sx={{ width: "100%" }}>
      <Paper elevation={3}>
        <Box p={2}>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            {/* Total Display (optional - moved or kept separately) */}
            <Grid item xs={12} sm={2} md={2}>
              <Typography variant="subtitle2" color="textSecondary">
                Total: {total}
              </Typography>
            </Grid>

            {/* Search and Date Range */}
            <Grid
              item
              xs={12}
              sm={12}
              md={10}
              container
              spacing={1}
              justifyContent="flex-end"
              alignItems="center"
            >
              <Grid item xs={12} sm={3} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Search By</InputLabel>
                  <Select
                    value={searchBy}
                    label="Search By"
                    onChange={(e) => {
                      setSearchBy(e.target.value);
                      setCurrentPage(0);
                    }}
                  >
                    <MenuItem value="patient">Patient</MenuItem>
                    <MenuItem value="doctor">Doctor</MenuItem>
                    <MenuItem value="online">Online Appointment</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3} md={3}>
                <TextField
                  label={
                    searchBy === "patient"
                      ? "Search Patient Name"
                      : searchBy === "doctor"
                      ? "Search Doctor Name"
                      : "Search Online Appointment"
                  }
                  variant="outlined"
                  value={search}
                  onChange={handleSearch}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={3} md={1.2}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={handleOpenDateDialog}
                  sx={{ height: 40, textTransform: "none" }}
                  startIcon={<CalendarTodayIcon />}
                >
                  Date
                </Button>
              </Grid>
              <Grid item xs={12} sm={3} md={1.2}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  onClick={handleReset}
                  sx={{ height: 40, textTransform: "none" }}
                  startIcon={<RestartAltIcon />}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>

            {/* Date Range Dialog */}
            <Dialog
              open={openDateDialog}
              onClose={handleCloseDateDialog}
              fullWidth
              maxWidth="md"
            >
              <DialogTitle>
                <Grid
                  container
                  alignItems="center"
                  justifyContent="center"
                  position="relative"
                >
                  <Grid item>
                    <Typography
                      variant="h6"
                      sx={{ color: "primary.main", textAlign: "center" }}
                    >
                      Select Date Range
                    </Typography>
                  </Grid>

                  <IconButton
                    onClick={handleCloseDateDialog}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Grid>
              </DialogTitle>

              <DialogContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <BasicRangeShortcuts
                      setStartDate={setStartDate}
                      setEndDate={setEndDate}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  </Grid>
                </Grid>
              </DialogContent>
            </Dialog>
          </Grid>

          <TableContainer
            component={Paper}
            sx={{ display: { xs: "none", sm: "block" } }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  {["Sno", "Patient", "Doctor", "Date", "Time", "Status"].map(
                    (head) => (
                      <TableCell
                        key={head}
                        sx={{ color: "white", fontWeight: "bold" }}
                      >
                        {head}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : appointments.length > 0 ? (
                  appointments.map((item, idx) => {
                    const isGuest = item.patient_name === "N/A";
                    return (
                      <TableRow
                        key={item.id}
                        hover
                        sx={
                          isGuest
                            ? { backgroundColor: "#fff9c4" } // light yellow
                            : {}
                        }
                      >
                        <TableCell>{currentPage * perPage + idx + 1}</TableCell>
                        <TableCell>
                          {isGuest ? (
                            <strong style={{ color: "#f57c00" }}>
                              {item.guest_patient}
                            </strong>
                          ) : (
                            item.patient_name
                          )}
                        </TableCell>
                        <TableCell>{item.doctor_name}</TableCell>
                        <TableCell>{item.date}</TableCell>
                        <TableCell>{item.time}</TableCell>
                        <TableCell>{item.status}</TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      No appointments found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* For small devices, show box layout */}
          <Box sx={{ display: { xs: "block", sm: "none" }, p: 1 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : appointments.length > 0 ? (
              appointments.map((item, idx) => {
                const isGuest = item.patient_name === "N/A";
                return (
                  <Paper
                    key={item.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      backgroundColor: isGuest ? "#fff9c4" : "white", // light yellow for guest
                    }}
                  >
                    <Typography variant="h6">
                      Appointment {currentPage * perPage + idx + 1}
                    </Typography>

                    <Typography variant="body1">
                      Patient:{" "}
                      {isGuest ? (
                        <strong style={{ color: "#f57c00" }}>
                          {item.guest_patient}
                        </strong>
                      ) : (
                        item.patient_name
                      )}
                    </Typography>

                    <Typography variant="body1">
                      Doctor: {item.doctor_name}
                    </Typography>
                    <Typography variant="body1">Date: {item.date}</Typography>
                    <Typography variant="body1">Time: {item.time}</Typography>
                    <Typography variant="body1">
                      Status: {item.status}
                    </Typography>
                  </Paper>
                );
              })
            ) : (
              <Typography align="center" sx={{ py: 4 }}>
                No appointments found.
              </Typography>
            )}
          </Box>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={total}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={perPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 20, 50, 100]}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default AppointmentTable;
